// Step 2: 场景选择 — Apple 浅色风格
import {
  Monitor, Building2, GraduationCap, Sun,
  Trophy, Theater, Sparkles, Clapperboard, Check
} from 'lucide-react';
import type { RegionId } from './RegionStep';
import { REGIONS } from './RegionStep';

/** 场景默认参数预设 — 场景决定初始观看距离和光线 */
export const SCENARIO_DEFAULTS: Record<string, { viewingDistance: number; lighting: string }> = {
  command_center: { viewingDistance: 4, lighting: 'dark' },       // 指挥中心：近距离暗室
  commercial:     { viewingDistance: 6, lighting: 'indoor' },     // 商业显示：室内标准
  conference:     { viewingDistance: 5, lighting: 'indoor' },     // 会议教育：中等距离室内
  outdoor:        { viewingDistance: 20, lighting: 'outdoor' },   // 户外广告：远距离户外直射
  sports:         { viewingDistance: 30, lighting: 'semi_outdoor' }, // 体育赛事：远距离半户外
  rental:         { viewingDistance: 10, lighting: 'semi_outdoor' }, // 租赁舞台：中远距离半户外
  creative:       { viewingDistance: 5, lighting: 'indoor' },     // 创意显示：近距离室内
  cinema:         { viewingDistance: 3, lighting: 'dark' },       // 影院XR：近距离暗室
};

/** 8 大行业场景 */
const SCENARIOS = [
  {
    id: 'command_center',
    name: '专业显示 / 指挥中心',
    nameEn: 'Command Center',
    icon: Monitor,
    color: '#0071e3',
    venues: ['指挥中心', '监控室', '数据中心'],
    features: '超小间距 · 7×24 稳定运行',
    recommendedPitch: '≤ P1.5',
  },
  {
    id: 'commercial',
    name: '商业显示',
    nameEn: 'Commercial Display',
    icon: Building2,
    color: '#34c759',
    venues: ['大堂', '展厅', '营业厅', '酒店'],
    features: '轻薄美观 · 安装简便',
    recommendedPitch: 'P1.5 - P2.5',
  },
  {
    id: 'conference',
    name: '会议 / 教育',
    nameEn: 'Conference & Education',
    icon: GraduationCap,
    color: '#5856d6',
    venues: ['会议室', '报告厅', '智慧教室'],
    features: '标准尺寸 · 交互触控',
    recommendedPitch: 'P1.2 - P2.0',
  },
  {
    id: 'outdoor',
    name: '户外广告',
    nameEn: 'Outdoor Advertising',
    icon: Sun,
    color: '#ff9500',
    venues: ['户外广告', '广场', '建筑立面'],
    features: '高亮度 ≥4000nits · IP65',
    recommendedPitch: 'P3.0 - P6.0',
  },
  {
    id: 'sports',
    name: '体育赛事',
    nameEn: 'Sports & Stadium',
    icon: Trophy,
    color: '#ff3b30',
    venues: ['体育场馆', '运动公园', '赛事直播'],
    features: '大面积拼接 · 高刷新率',
    recommendedPitch: 'P3.0 - P5.0',
  },
  {
    id: 'rental',
    name: '租赁舞台',
    nameEn: 'Rental & Staging',
    icon: Theater,
    color: '#af52de',
    venues: ['演唱会', '展会', '发布会'],
    features: '快速搭建 · 轻量化箱体',
    recommendedPitch: 'P2.5 - P4.0',
  },
  {
    id: 'creative',
    name: '创意显示',
    nameEn: 'Creative Display',
    icon: Sparkles,
    color: '#ff2d55',
    venues: ['沉浸式', '异形屏', '透明屏'],
    features: '柔性定制 · 造型多变',
    recommendedPitch: 'P1.5 - P3.0',
  },
  {
    id: 'cinema',
    name: '影院 / XR虚拟制作',
    nameEn: 'Cinema & Virtual Production',
    icon: Clapperboard,
    color: '#007aff',
    venues: ['电影院', 'XR 虚拟制作', '演播室'],
    features: '极致画质 · 高色域宽容度',
    recommendedPitch: '≤ P1.2',
  },
] as const;

export type ScenarioId = typeof SCENARIOS[number]['id'];

interface ScenarioStepProps {
  selectedRegion: RegionId;
  selectedScenario: ScenarioId | null;
  onSelect: (scenario: ScenarioId) => void;
}

export function ScenarioStep({ selectedRegion, selectedScenario, onSelect }: ScenarioStepProps) {
  const regionName = REGIONS.find(r => r.id === selectedRegion)?.name ?? '';

  return (
    <div className="animate-in fade-in-0 duration-500">
      {/* 面包屑 */}
      <div className="flex items-center gap-1.5 text-[13px] text-[#86868b] mb-6">
        <span className="text-[#0071e3] font-medium">{regionName}</span>
        <span>›</span>
        <span>选择场景</span>
      </div>

      {/* 场景卡片 2×4 网格 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
        {SCENARIOS.map((scenario, index) => {
          const isSelected = selectedScenario === scenario.id;
          const Icon = scenario.icon;
          return (
            <button
              key={scenario.id}
              onClick={() => onSelect(scenario.id)}
              className={`
                group relative flex flex-col items-start p-5 rounded-2xl border text-left transition-all duration-300
                ${isSelected
                  ? 'border-[#0071e3] bg-[#0071e3]/[0.04] shadow-[0_0_0_1px_#0071e3]'
                  : 'border-gray-200/80 bg-white hover:border-gray-300 hover:shadow-md'
                }
              `}
              style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'both' }}
            >
              {/* 选中标记 */}
              {isSelected && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#0071e3] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
              )}

              {/* 图标 */}
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: `${scenario.color}15` }}
              >
                <Icon className="w-5 h-5" style={{ color: scenario.color }} />
              </div>

              {/* 场景名称 */}
              <h3 className={`text-[15px] font-semibold mb-0.5 ${
                isSelected ? 'text-[#0071e3]' : 'text-[#1d1d1f]'
              }`}>
                {scenario.name}
              </h3>
              <span className="text-[11px] text-[#86868b] mb-3">{scenario.nameEn}</span>

              {/* 子场景标签 */}
              <div className="flex flex-wrap gap-1 mb-3">
                {scenario.venues.map(v => (
                  <span key={v} className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-[#86868b]">
                    {v}
                  </span>
                ))}
              </div>

              {/* 关键特征 */}
              <p className="text-[12px] text-[#86868b] mt-auto">
                {scenario.features}
              </p>

              {/* 推荐间距 */}
              <div className="mt-2 text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-[#86868b] border border-gray-100">
                推荐间距 {scenario.recommendedPitch}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { SCENARIOS };
export default ScenarioStep;
